import type { DefLookup } from "../catalog";
import { CONTROL_CIRCUITS, CONTROL_CONTACT_GROUPS } from "../catalog/control-circuits";
import { computeSystemCableSpecs } from "../cable-spec";
import { OVERLOAD_TOLERANCE } from "../cable-current";
import { isAcTerminalId } from "../ac";
import { buildDcCircuitTopology, type DcSourcePair } from "../dc-circuit-topology";
import { effectiveTerminals } from "../effective";
import { isSenseTerminal } from "../sense-terminal";
import type { ComponentDefinition, Issue, System } from "../types";
import { controlDesignSystems, controlDesignStatesTruncated, fixedControlDesignScenario } from "./control-design-states";
import { buildGraph, v } from "./graph";
import { circuitReach, matchesCircuitDefinition } from "./circuit-component-utils";

/** Nominal system class only, shared with the existing voltage diagnostics.
 * This band classifies 12.8 V battery / charging declarations; it is NOT a
 * manufacturer operating-voltage tolerance for the switch or relay coil. */
function isTwelveVoltClass(voltage: number): boolean {
  return voltage >= 10 && voltage <= 15;
}

function declaredControlSourceVoltage(source: DcSourcePair, system: System, lookup: DefLookup): number | undefined {
  const definition = (id: string | undefined) => {
    const node = system.nodes.find(n => n.id === id);
    return node?.kind === "component" ? lookup(node.definitionId, node.customDefinition) : undefined;
  };
  if (source.kind === "converter-output") {
    const def = definition(source.nodeId);
    const voltage = def?.outputVoltage ?? def?.voltage;
    return voltage != null && Number.isFinite(voltage) && voltage > 0 ? voltage : undefined;
  }
  let total = 0;
  for (const level of source.levels) {
    const volts = level.map(id => definition(id)?.voltage);
    const reference = volts[0];
    if (reference == null || !Number.isFinite(reference) || reference <= 0 || volts.some(value => value !== reference)) return undefined;
    total += reference;
  }
  return total > 0 ? total : undefined;
}

function controlVoltageIssues(system: System, lookup: DefLookup, nodeId: string, def: ComponentDefinition, scenarios: System[]): Issue[] {
  const relay = def.id === "dist-relay-spdt-12v";
  const rated = def.id === "dist-dpdt-center-off" ? 30 : 12;
  let incompatible: number | undefined;
  for (const selected of scenarios.length ? scenarios : [system]) {
    const physical = buildGraph(selected, lookup);
    const valid = new Set<string>();
    for (const node of selected.nodes) {
      if (node.kind !== "component") continue;
      const part = lookup(node.definitionId, node.customDefinition);
      if (!part) continue;
      for (const post of effectiveTerminals(part, node)) {
        // Only this reviewed coil may join the power evidence through signal
        // pins. Sense taps, data ports, AC and fictitious posts cannot supply it.
        if (!isAcTerminalId(post.id) && (!isSenseTerminal(part, post) ||
            (relay && node.id === nodeId && ["85", "86"].includes(post.id)))) valid.add(v(node.id, post.id));
      }
    }
    const reach = (post: string) => circuitReach(physical, v(nodeId, post),
      (from, edge) => valid.has(from) && valid.has(edge.vertex));
    const positive = relay ? reach("86") : new Set(CONTROL_CONTACT_GROUPS[def.id].flatMap(group =>
      group.flatMap(post => [...reach(post)])));
    const negative = relay ? reach("85") : undefined;
    for (const source of buildDcCircuitTopology(selected, lookup).sources) {
      // A contact can be exposed to a supply with the load switched off. A
      // coil requires both conductors of the SAME source, never an unrelated
      // bank's negative. Reversed pins retain the same voltage magnitude.
      const forward = source.positive.some(post => positive.has(post)) &&
        (relay ? source.negative.some(post => negative!.has(post)) : true);
      const reverse = relay && source.positive.some(post => negative!.has(post)) && source.negative.some(post => positive.has(post));
      if ((!forward && !reverse) || source.positive.some(post => positive.has(post) && negative?.has(post)) ||
          source.negative.some(post => positive.has(post) && negative?.has(post))) continue;
      const actual = declaredControlSourceVoltage(source, selected, lookup);
      if (actual == null || (rated === 30 ? actual <= 30 : isTwelveVoltClass(actual))) continue;
      incompatible = Math.max(incompatible ?? 0, actual);
    }
  }
  if (incompatible == null) return [];
  const node = system.nodes.find(n => n.id === nodeId)!;
  const label = node.kind === "component" ? node.label ?? def.shortName ?? def.name : def.name;
  const code = relay ? "control-coil-voltage-mismatch" : "control-contact-voltage-unverified";
  const vars = { label, actual: incompatible, rated };
  return [{ code, severity: "warning", title: { key: `diag.${code}.title`, vars },
    message: { key: `diag.${code}.message`, vars }, fix: { key: `diag.${code}.fix`, vars },
    nodeIds: [nodeId], cableIds: system.cables.filter(c =>
      (c.fromNodeId === nodeId && (!relay || ["85", "86"].includes(c.fromTerminalId))) ||
      (c.toNodeId === nodeId && (!relay || ["85", "86"].includes(c.toTerminalId))))
      .map(c => c.id),
  }];
}

/** Contact continuity only. A coil is not a conductor between its pins or to
 * contact 30, and its presence cannot prove automatic operation. */
export function runControlCircuitDiagnostics(system: System, lookup: DefLookup): Issue[] {
  const issues: Issue[] = [];
  const graph = buildGraph(system, lookup);
  const alternatives = controlDesignSystems(system, lookup);
  const specsByScenario = new Map<System, ReturnType<typeof computeSystemCableSpecs>>();
  if (controlDesignStatesTruncated(system, lookup)) issues.push({
    code: "control-state-coverage-unverified", severity: "warning",
    title: { key: "diag.control-state-coverage-unverified.title" },
    message: { key: "diag.control-state-coverage-unverified.message" },
    fix: { key: "diag.control-state-coverage-unverified.fix" },
  });
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    const baseline = CONTROL_CIRCUITS.find(part => part.id === def?.id);
    if (!def || !baseline) continue;
    const cables = system.cables.filter(c => c.fromNodeId === node.id || c.toNodeId === node.id);
    if (!cables.length) continue;
    const vars = { label: node.label ?? def.shortName ?? def.name };
    const issue = (code: string, severity: Issue["severity"]) => issues.push({
      code, severity, title: { key: `diag.${code}.title`, vars },
      message: { key: `diag.${code}.message`, vars }, fix: { key: `diag.${code}.fix`, vars },
      nodeIds: [node.id], cableIds: cables.map(c => c.id),
    });
    if (!matchesCircuitDefinition(def, baseline)) {
      issue("control-model-unverified", "info");
      continue;
    }
    issues.push(...controlVoltageIssues(system, lookup, node.id, def, alternatives));
    if (def.id === "dist-relay-spdt-12v") {
      issue("control-relay-manual", "info");
      const wired = (terminal: string) => cables.some(c =>
        (c.fromNodeId === node.id && c.fromTerminalId === terminal) ||
        (c.toNodeId === node.id && c.toTerminalId === terminal));
      if (wired("85") !== wired("86")) issue("control-coil-incomplete", "warning");
    } else {
      issue("control-state-sizing-unverified", "info");
    }
    const groups = CONTROL_CONTACT_GROUPS[def.id];
    // Joining the two throws can be intentional (bilge auto/manual wiring).
    // A bypass exists only when the common reaches a throw outside this part.
    const bypass = groups.some(([common, ...throws]) => {
      const reached = circuitReach(graph, v(node.id, common),
        (_, { via }) => via.kind !== "intra" || via.link.nodeId !== node.id);
      return throws.some(other => reached.has(v(node.id, other)));
    });
    if (bypass) issue("control-contact-bypass", "warning");
    // Do not combine normally-open and normally-closed contacts into one bus
    // for sizing. Evaluate each intended position against the closed-loop
    // current model, including branches not active in the default position.
    for (const option of def.simulateStates?.options ?? []) {
      const selectedPosts = new Set(option.intraLinks?.flat() ?? []);
      if (!selectedPosts.size) continue;
      // A fresh design snapshot retains complete-route provenance while changing
      // only this instance's selected copper. Simulation suppresses that
      // provenance and must not be mistaken for a complete design check.
      const scenario = fixedControlDesignScenario(system, new Map([[node.id,
        { ...def, simulateStates: { ...def.simulateStates!, default: option.id } },
      ]]));
      const scenarios = alternatives.filter(candidate => {
        const item = candidate.nodes.find(n => n.id === node.id);
        return item?.kind === "component" && lookup(item.definitionId, item.customDefinition)?.simulateStates?.default === option.id;
      });
      const overloaded: Array<{ id: string; current: number; maximum: number }> = [];
      for (const selected of scenarios.length ? scenarios : [scenario]) {
        const specs = specsByScenario.get(selected) ?? computeSystemCableSpecs(selected, lookup);
        specsByScenario.set(selected, specs);
        for (const run of cables) {
          const postId = run.fromNodeId === node.id ? run.fromTerminalId : run.toTerminalId;
          if (!selectedPosts.has(postId)) continue;
          const post = def.terminals.find(t => t.id === postId);
          const spec = specs.get(run.id);
          // Unknown or provisional current is covered by the explicit model
          // limitation; it must never become a verified zero-current pass.
          if (!post || !spec || spec.currentBasis !== "complete-route-envelope") continue;
          const maximum = Math.min(post.maxAmp > 0 ? post.maxAmp : Infinity,
            run.wireSizeMm2Override != null && !spec.sizingVerificationRequired ? spec.wire.ampacity : Infinity);
          if (spec.continuous > maximum * OVERLOAD_TOLERANCE) overloaded.push({ id: run.id, current: spec.continuous, maximum });
        }
      }
      if (overloaded.length) {
        const worst = overloaded.sort((a, b) => b.current / b.maximum - a.current / a.maximum)[0];
        const stateVars = { ...vars, position: option.label, current: Number(worst.current.toFixed(2)), maxCurrent: worst.maximum };
        issues.push({
          code: "control-contact-overload", severity: "warning",
          title: { key: "diag.control-contact-overload.title", vars: stateVars },
          message: { key: "diag.control-contact-overload.message", vars: stateVars },
          fix: { key: "diag.control-contact-overload.fix", vars: stateVars },
          nodeIds: [node.id], cableIds: [...new Set(overloaded.map(item => item.id))],
        });
      }
    }
  }
  return issues;
}
